import React from 'react';
import { Badge } from '../ui';

/**
 * Componente para mostrar el badge de origen de datos
 * @param {Object} dataItem - El item de datos que puede contener la propiedad 'source'
 * @param {Object} options - Opciones de configuración del badge
 * @param {string} options.position - Posición del badge ('bottom-right', 'top-right', 'bottom-left', 'top-left')
 * @param {string} options.size - Tamaño del badge ('small', 'medium', 'large')
 * @param {string} options.variant - Variante de estilo ('muted', 'primary', 'secondary')
 * @param {boolean} options.showTooltip - Si mostrar tooltip con información adicional
 * @param {Object} options.customMapping - Mapeo personalizado de fuentes
 * @param {Array} options.excludeSources - Array de fuentes a excluir del badge (ej: ['retail', 'manual'])
 */
const DataSourceBadge = ({ 
  dataItem, 
  options = {} 
}) => {
  // Configuración por defecto
  const {
    position = 'bottom-right',
    size = 'small',
    variant = 'muted',
    showTooltip = true,
    customMapping = null,
    excludeSources = ['retail']
  } = options;

  // Mapeo centralizado de fuentes de datos
  const defaultMapping = {
    'siisa': 'SIISA',
    'manual': 'Manual',
    'bcra': 'BCRA',
    'afip': 'AFIP',
    'external': 'Externo',
    'system': 'Sistema',
    'user': 'Usuario',
    'api': 'API',
    'database': 'Base de Datos',
    'import': 'Importación',
    'cirenio': 'Cirenio',
    'retail': 'Retail'
  };

  // Usar mapeo personalizado si se proporciona, sino el por defecto
  const dataSourceMapping = customMapping || defaultMapping;

  // Función para obtener el nombre legible de la fuente
  const getDataSourceLabel = (source) => {
    if (!source) return null;
    const normalizedSource = source.toLowerCase().trim();
    return dataSourceMapping[normalizedSource] || source;
  };

  // Si no hay datos o no hay source, no renderizar nada
  if (!dataItem || !dataItem.source) return null;
  
  // Verificar si la fuente está en la lista de exclusión
  const normalizedSource = dataItem.source.toLowerCase().trim();
  if (excludeSources.includes(normalizedSource)) return null;
  
  const sourceLabel = getDataSourceLabel(dataItem.source);
  if (!sourceLabel) return null;

  const getPositionClass = () => {
    switch (position) {
      case 'top-right':
        return 'rg-badge-source--top-right';
      case 'top-left':
        return 'rg-badge-source--top-left';
      case 'bottom-left':
        return 'rg-badge-source--bottom-left';
      case 'bottom-right':
      default:
        return 'rg-badge-source--bottom-right';
    }
  };

  const getVariant = () => {
    switch (variant) {
      case 'primary':
        return 'primary';
      case 'secondary':
        return 'secondary';
      case 'muted':
      default:
        return 'muted';
    }
  };

  const getSize = () => {
    switch (size) {
      case 'large':
      case 'medium':
        return 'md';
      case 'small':
      default:
        return 'sm';
    }
  };

  // Información adicional para el tooltip
  const getTooltipContent = () => {
    if (!showTooltip) return null;
    
    const additionalInfo = [];
    if (dataItem.created_at) {
      additionalInfo.push(`Creado: ${dataItem.created_at}`);
    }
    if (dataItem.updated_at) {
      additionalInfo.push(`Actualizado: ${dataItem.updated_at}`);
    }
    
    let tooltipText = `Dato proveniente de: ${sourceLabel}`;
    if (additionalInfo.length > 0) {
      tooltipText += `\n${additionalInfo.join('\n')}`;
    }
    
    return tooltipText;
  };

  return (
    <Badge
      variant={getVariant()}
      size={getSize()}
      className={`rg-badge-source ${getPositionClass()}`}
      title={getTooltipContent()}
      data-testid="data-source-badge"
    >
      {sourceLabel}
    </Badge>
  );
};

export default DataSourceBadge;
